/**
 * PATH: apps/qa/src/pages/record-detail/useQaQuoteBrand.ts
 * FILE_ID: QA-RECORD-QUOTE-BRAND-001
 * PURPOSE: Publish the org letterhead into the quote-brand slot for QA Hub.
 * WHY: Dashboard TenantBootstrap is the only other publisher. QA Hub is a
 *      separate shell, so ITP Print and Save PDF stayed unbranded there.
 */
import { useEffect } from 'react'
import { getConfiguredClient } from '@quotech/api'
import {
  brandFromSettings,
  type OrganisationBrandingPayload,
} from '@quotech/quote-builder/branding/types'
import {
  getQuoteBuilderQuoteBrand,
  setQuoteBuilderQuoteBrand,
} from '@quotech/quote-builder/runtime/quoteBrand'

function text(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

/** Same fields as quoteBrandPayloadFromBootstrap in the dashboard shell. */
export function qaBootstrapBrandPayload(data: unknown): OrganisationBrandingPayload | null {
  if (!data || typeof data !== 'object') return null
  const row = data as {
    organisation?: Record<string, unknown>
    branding?: Record<string, unknown>
  }
  const org = row.organisation
  const branding = row.branding
  if (!org && !branding) return null
  const height = branding?.logo_display_height_px
  return {
    company_name: text(org?.company_name) || text(org?.name),
    abn: text(org?.abn),
    address: text(org?.address),
    phone: text(org?.phone),
    tagline: text(branding?.tagline),
    logo_url: text(branding?.logo_url),
    logo_data_uri: text(branding?.logo_data_uri),
    logo_display_height_px: typeof height === 'number' ? height : null,
    brand_color_primary: text(branding?.color_primary),
    brand_color_secondary: text(branding?.color_secondary),
  }
}

export function useQaQuoteBrand(): void {
  useEffect(() => {
    let cancelled = false
    let client: { get: (url: string) => Promise<{ data?: unknown }> }
    try {
      client = getConfiguredClient()
    } catch {
      return
    }
    client.get('/api/tenants/me/bootstrap/').then((response) => {
      if (cancelled) return
      const payload = qaBootstrapBrandPayload(response?.data)
      if (!payload) return
      setQuoteBuilderQuoteBrand(brandFromSettings(payload, getQuoteBuilderQuoteBrand()))
    }).catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])
}
